import React from "react";
import { Box, Typography, Container, Grid, Paper, Button } from "@mui/material";
import { motion } from "framer-motion";
import ResponsiveAppBar from "../components/NavBar";
import Link from "@mui/material/Link";
import Sponsor1Logo from "../assets/logo1.png";
import IvishLogo from "../assets/Ivish.png";

// Animations
const sectionVariant = {
  hidden: { opacity: 0, y: 45 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const staggerChildren = {
  visible: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

// FONT STYLES
const font = `"Montserrat", sans-serif`;

const Hackathon = () => {
  const heroStyles = {
    backgroundImage: `linear-gradient(rgba(0,0,0,0.75), rgba(0,0,0,0.75)),
      url("https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=2070&q=80")`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    color: "#fff",
    textAlign: "center",
    py: 14,
    mb: 7,
  };

  const sectionStyles = {
    p: 5,
    bgcolor: "#1A1E21",
    color: "white",
    mb: 7,
    borderRadius: "14px",
    fontFamily: font,
    boxShadow: "0px 5px 30px rgba(0,0,0,0.45)",
  };

  const titleStyles = {
    color: "#FFD700",
    mb: 3,
    pb: 1,
    fontWeight: 800,
    fontSize: "34px",
    letterSpacing: "0.5px",
    fontFamily: font,
    display: "inline-block",
    borderBottom: "3px solid rgba(255,215,0,0.4)",
  };

  const buttonStyles = {
    bgcolor: "#FFD700",
    color: "#000",
    fontWeight: 700,
    borderRadius: "12px",
    px: 5,
    py: 1.4,
    fontSize: "17px",
    fontFamily: font,
    transition: "0.3s",
    "&:hover": {
      bgcolor: "#FFE55C",
      transform: "scale(1.09)",
      boxShadow: "0 0 18px #FFD700",
    },
  };

  // SPONSORS WITH CLICKABLE LINKS
  const sponsors = [
    { id: 1, logoUrl: Sponsor1Logo, name: "Inject Solar LLP", url: "https://injectsolar.com" },
    { id: 2, logoUrl: IvishLogo, name: "Ivish AI", url: "https://ivishai.com" },
  ];

  return (
    <Box sx={{ bgcolor: "#242B2E", minHeight: "100vh", fontFamily: font }}>
      <ResponsiveAppBar />

      {/* HERO SECTION */}
      <Box id="home" sx={heroStyles}>
        <Container>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 900,
                mb: 2,
                fontSize: "58px",
                fontFamily: font,
                letterSpacing: "-0.5px",
              }}
            >
              WADLA Hackathon 2025
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.7 }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 2, fontSize: "20px", fontFamily: font, fontWeight: 400 }}
            >
              Build. Compete. Innovate. Onsite.
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: "#FFD700",
                fontWeight: 700,
                fontSize: "20px",
                mb: 3,
                fontFamily: font,
              }}
            >
              18 – 19 December 2025 • PDPM IIITDM Jabalpur
            </Typography>

            <Typography
              variant="body1"
              sx={{
                maxWidth: "780px",
                mx: "auto",
                mb: 3,
                fontSize: "17px",
                opacity: 0.9,
                lineHeight: 1.6,
                fontFamily: font,
              }}
            >
              A high-intensity, offline 24-hour deep learning challenge where top
              minds solve real-world problems under pressure.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container>

        {/* ABOUT SECTION */}
        <motion.div variants={sectionVariant} initial="hidden" whileInView="visible">
          <Box id="about" sx={sectionStyles}>
            <Typography sx={titleStyles}>About the Hackathon</Typography>

            <Typography
              variant="body1"
              sx={{ fontSize: "17px", lineHeight: 1.7, mb: 3, opacity: 0.95 }}
            >
              WADLA Hackathon 2025 brings innovators together to build
              AI-powered prototypes tackling real-world problems. All datasets and
              statements will be revealed onsite.
            </Typography>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h6"
                  sx={{ color: "#FFD700", fontWeight: 700, mb: 2 }}
                >
                  Who Can Participate
                </Typography>

                <motion.ul
                  variants={staggerChildren}
                  initial="hidden"
                  whileInView="visible"
                  style={{ paddingLeft: "20px", fontSize: "17px", lineHeight: 1.6 }}
                >
                  {[
                    "AI & Deep Learning Enthusiasts",
                    "UG Students (1st–4th Year)",
                    "PG Students (M.Tech / M.Sc)",
                    "Cross-college Teams Allowed",
                    "Team Size: 2–3 Members",
                    "Mandatory In-Person Participation",
                  ].map((item, index) => (
                    <motion.li
                      key={index}
                      variants={{
                        hidden: { opacity: 0, x: -20 },
                        visible: { opacity: 1, x: 0 },
                      }}
                      style={{ marginBottom: "6px" }}
                    >
                      {item}
                    </motion.li>
                  ))}
                </motion.ul>
              </Grid>
            </Grid>

            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Button sx={buttonStyles} href="https://forms.gle/JpW2WdJdQfijv54CA">
                Register Now
              </Button>
            </Box>
          </Box>
        </motion.div>

        {/* THEMES SECTION */}
        <motion.div variants={sectionVariant} initial="hidden" whileInView="visible">
          <Box id="tracks" sx={sectionStyles}>
            <Typography sx={titleStyles}>Themes (Revealed Onsite)</Typography>

            <Grid container spacing={4}>
              {[
                "AI for Travel & Intelligent Mobility",
                "AI for Agriculture & Smart Farming",
                "AI for Solar Energy & Sustainability",
                "Theme to be announced",
                "Theme to be announced",
                "Theme to be announced",
              ].map((theme, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <motion.div whileHover={{ scale: 1.04 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#FFD700",
                        fontSize: "20px",
                        fontWeight: 600,
                      }}
                    >
                      {i + 1}. {theme}
                    </Typography>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>

        {/* REWARDS SECTION */}
        <motion.div variants={sectionVariant} initial="hidden" whileInView="visible">
          <Box id="rewards-opportunities" sx={sectionStyles}>
            <Typography sx={titleStyles}>Rewards & Opportunities</Typography>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography sx={{ color: "#FFD700", fontWeight: 700, mb: 1 }}>
                  Prize Pool:
                </Typography>
                <ul style={{ paddingLeft: 20, fontSize: "17px" }}>
                  <li>₹50,000 Total</li>
                </ul>

                <Typography sx={{ color: "#FFD700", fontWeight: 700, mt: 3 }}>
                  Special Perks:
                </Typography>
                <ul style={{ paddingLeft: 20, fontSize: "17px" }}>
                  <li>B.Tech 1st–3rd Year: Internship / FTP</li>
                  <li>4th Year: Internship + FTP</li>
                </ul>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography sx={{ color: "#FFD700", fontWeight: 700 }}>
                  Additional Benefits:
                </Typography>
                <ul style={{ paddingLeft: 20, fontSize: "17px" }}>
                  <li>Certificates</li>
                  <li>Industry Mentor Access</li>
                  <li>Recognition for All Finalists</li>
                </ul>
              </Grid>
            </Grid>
          </Box>
        </motion.div>

        {/* SPONSORS WITH CLICK FUNCTIONALITY */}
        <motion.div variants={sectionVariant} initial="hidden" whileInView="visible">
          <Box id="prizes" sx={sectionStyles}>
            <Typography sx={titleStyles}>Industry Collaborators</Typography>

            <Grid container spacing={4}>
              {sponsors.map((s) => (
                <Grid item xs={6} sm={3} key={s.id}>
                  <motion.div whileHover={{ scale: 1.08 }}>
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "none" }}
                    >
                      <Paper
                        sx={{
                          p: 2,
                          height: 130,
                          bgcolor: "white",
                          borderRadius: "14px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 0 15px rgba(255,215,0,0.3)",
                          cursor: "pointer",
                        }}
                      >
                        <img
                          src={s.logoUrl}
                          alt={s.name}
                          style={{ maxWidth: "90%", maxHeight: "90%" }}
                        />
                      </Paper>
                    </a>
                  </motion.div>

                  <Typography
                    variant="body1"
                    sx={{
                      textAlign: "center",
                      mt: 1.5,
                      fontWeight: 700,
                      color: "white",
                      fontSize: "17px",
                    }}
                  >
                    {s.name}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>

        {/* HOW IT WORKS */}
        <motion.div variants={sectionVariant} initial="hidden" whileInView="visible">
          <Box id="how-it-works" sx={sectionStyles}>
            <Typography sx={titleStyles}>How the Hackathon Works</Typography>

            <ol style={{ paddingLeft: 24, fontSize: "17px", lineHeight: 1.8 }}>
              <li>Form a team (2–3 members)</li>
              <li>Register via the official link</li>
              <li>Attend the onsite kickoff</li>
              <li>Receive datasets & problem statements</li>
              <li>Develop your solution in 24 hours</li>
              <li>Present to judges</li>
            </ol>
          </Box>
        </motion.div>

        {/* REGISTRATION */}
        <motion.div variants={sectionVariant} initial="hidden" whileInView="visible">
          <Box id="registration" sx={sectionStyles}>
            <Typography sx={titleStyles}>Registration</Typography>

            <ul style={{ paddingLeft: 24, fontSize: "17px", lineHeight: 1.7 }}>
              <li>₹1199 per Team</li>
              <li>Team Size: 2–3 Members</li>
              <li>
                Link:{" "}
                <a
                  href="https://wadla.in/hackathon"
                  style={{ color: "#FFD700", fontWeight: 700 }}
                >
                  https://wadla.in/hackathon
                </a>
              </li>
            </ul>
          </Box>
        </motion.div>

        {/* VENUE */}
        <motion.div variants={sectionVariant} initial="hidden" whileInView="visible">
          <Box id="venue" sx={sectionStyles}>
            <Typography sx={titleStyles}>Venue</Typography>

            <Typography variant="h6" sx={{ color: "#FFD700", fontWeight: 700 }}>
              PDPM IIITDM Jabalpur
            </Typography>

            <Typography sx={{ lineHeight: 1.7, mt: 1, fontSize: "17px" }}>
              Near Dumna Airport Road <br />
              Post Khamaria <br />
              Jabalpur, Madhya Pradesh 482005
            </Typography>
          </Box>
        </motion.div>

        {/* DELIVERABLES */}
        <motion.div variants={sectionVariant} initial="hidden" whileInView="visible">
          <Box id="deliverables" sx={sectionStyles}>
            <Typography sx={titleStyles}>Deliverables</Typography>

            <ul style={{ paddingLeft: 24, lineHeight: 1.7, fontSize: "17px" }}>
              <li>Working Prototype</li>
              <li>Final Presentation</li>
              <li>Live Demonstration</li>
            </ul>
          </Box>
        </motion.div>

        {/* FOOTER */}
        <motion.div variants={sectionVariant} initial="hidden" whileInView="visible">
          <Box sx={{ textAlign: "center", py: 5, color: "white" }}>
            <Typography
              variant="h4"
              sx={{ color: "#FFD700", mb: 3, fontWeight: 800, fontSize: "32px" }}
            >
              Ready to push boundaries?
            </Typography>

            <Typography
              variant="body1"
              sx={{
                maxWidth: 700,
                mx: "auto",
                mb: 3,
                fontSize: "17px",
                lineHeight: 1.7,
              }}
            >
              Register your team and take part in one of the most intense AI
              hackathons of the year.
            </Typography>

            <Button
              sx={buttonStyles}
              href="https://forms.gle/JpW2WdJdQfijv54CA"
            >
              Register Team
            </Button>
          </Box>

          <Box sx={{ textAlign: "center", py: 2, mt: 3, borderTop: "1px solid #333" }}>
            <Typography sx={{ color: "#888", fontSize: "15px" }}>
              © WADLA 2025 — Empowering Innovation Through Deep Learning
            </Typography>
          </Box>
        </motion.div>

      </Container>
    </Box>
  );
};

export default Hackathon;
